/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  Button,
  Chip,
  FormHelperText,
  Grid,
  MenuItem,
  Tooltip,
} from '@mui/material';
import React from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { ReactSortable } from 'react-sortablejs';
import {
  getDeepProperty,
  ReactHookFormHiddenInput,
  ReactHookFormSelect,
  ReactHookFormTextField,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import {
  AlertBanner,
  CustomIconButton,
  SwitchButton,
  useConfirmationDialog,
  usePreventBrowserClose,
} from '@opengeoweb/shared';
import type { MapPreset } from '@opengeoweb/store';
import { Add, AlertBadge, Edit } from '@opengeoweb/theme';
import { CollapseToggleButton } from './CollapseToggleButton';
import {
  buildDefaultWarningItem,
  getDefaultDayRangeForType,
  getMinSelectableDayForType,
  warningEventModes,
  warningEventTypes,
} from './types';
import { buildSubmitValues, getFormData } from './utils';
import { WarningEventItem } from './WarningEventItem';
import {
  getTimelineRange,
  WarningEventTimelineRangeProvider,
} from './WarningEventTimeline';
import type { FormError } from '../../../store/warningsEventForm/reducer';
import type {
  WarningEvent,
  WarningEventTexts,
} from '../../../store/warningsForm/types';
import {
  PublicWarningStatus,
  WarningEventMode,
  WarningEventType,
} from '../../../store/warningsForm/types';
import { useWarningsTranslation } from '../../../utils/i18n';
import { useFormDirty } from '../../../utils/useFormDirty';
import { getErrors, useScrollTopOnError } from '../../../utils/warningUtils';
import { Phenomenon } from '../../PublicWarningsForm/PhenomenonField';
import WarningAvatar from '../../PublicWarningsFormDialog/WarningsAvatar';
import {
  SUMMARY_CONCISE_SUMMARY_LIMIT,
  SUMMARY_DESCRIPTION_LIMIT,
  SUMMARY_HEADLINE_LIMIT,
} from '../WarningEventSummaryDialog/WarningEventSummaryForm';

const INCIDENT_MAX_LENGTH = 30;

const TEST_MESSAGE_PREFIX_ORIGINAL = 'DIT IS EEN TEST BERICHT';
const TEST_MESSAGE_PREFIX_TRANSLATION = 'THIS IS A TEST MESSAGE';

export const CROSS_FIELD_VALIDATIONS: Record<
  string,
  {
    fields: string[];
    translationKey: string;
  }
> = {
  'texts.headlineOriginal': {
    fields: ['texts.conciseSummaryOriginal', 'texts.descriptionOriginal'],
    translationKey: 'warning-event-validation-summary-incomplete',
  },
  'texts.headlineTranslation': {
    fields: ['texts.conciseSummaryTranslation', 'texts.descriptionTranslation'],
    translationKey: 'warning-event-validation-translation-incomplete',
  },
};

export const getCrossFieldError = (
  anchorField: string,
  errors: FieldErrors,
): string | undefined => {
  const rules = CROSS_FIELD_VALIDATIONS[anchorField];
  return rules.fields.some((field) => getDeepProperty(field.split('.'), errors))
    ? rules.translationKey
    : undefined;
};

const dayOptions = [
  { label: 'Today', value: 0 },
  { label: '+1', value: 1 },
  { label: '+2', value: 2 },
  { label: '+3', value: 3 },
  { label: '+4', value: 4 },
  { label: '+5', value: 5 },
  { label: '+6', value: 6 },
  { label: '+7', value: 7 },
];

const DaySelector: React.FC<{
  selectedDays: [number, number] | [];
  onToggleDay: (day: number) => void;
  isDisabled?: boolean;
  minSelectableDay?: number;
}> = ({
  selectedDays,
  onToggleDay,
  isDisabled = false,
  minSelectableDay = 0,
}) => {
  const chipSize = 32;
  return (
    <Box
      sx={{
        display: 'flex',
        width: chipSize * dayOptions.length,
        paddingTop: '12px',
      }}
    >
      {dayOptions.map((day) => {
        const isSelected =
          selectedDays.length === 2 &&
          day.value >= Math.min(...selectedDays) &&
          day.value <= Math.max(...selectedDays);
        const isDayDisabled = isDisabled || day.value < minSelectableDay;
        return (
          <Chip
            clickable={!isDayDisabled}
            color={isSelected ? 'primary' : 'default'}
            data-testid={`warning-day-chip-${day.value}`}
            disabled={isDayDisabled}
            key={day.value}
            label={day.label}
            onClick={() => onToggleDay(day.value)}
            size="small"
            sx={{
              borderRadius: 1,
              height: chipSize,
              width: chipSize,
              borderColor: 'transparent',
              mr: 0,
              ...(day.value === 0 && {
                fontSize: 8,
                '.MuiChip-label': { paddingX: 0 },
              }),
            }}
            variant={isSelected ? 'filled' : 'outlined'}
          />
        );
      })}
    </Box>
  );
};

export interface WarningEventFormContentProps {
  defaultMapPreset?: MapPreset;
  error?: FormError;
  currentEventEditor?: string;
  isSummaryDialogOpen?: boolean;
  selectedEvent?: WarningEvent;
  shouldHideFormToggleMode?: boolean;
  isReadOnly?: boolean;
  isSnapshot?: boolean;
  isDefaultCollapsed?: boolean;
  onCancel?: () => void;
  onChangeFormMode?: (isReadOnly: boolean) => void;
  onDelete?: () => void;
  onFormDirty?: (isFormDirty: boolean) => void;
  onOpenWarningSummary?: () => void;
  onSaveDraft?: (formValues: WarningEvent) => Promise<Headers | null>;
  onPublish?: (formValues: WarningEvent) => Promise<Headers | null>;
}

export const WarningEventFormContent: React.FC<
  WarningEventFormContentProps
> = ({
  defaultMapPreset,
  currentEventEditor,
  error = { severity: 'error', message: '' },
  shouldHideFormToggleMode = false,
  isReadOnly = false,
  isSnapshot = false,
  isDefaultCollapsed = false,
  onCancel = (): void => {},
  onChangeFormMode = (): void => {},
  onDelete = (): void => {},
  onFormDirty = (): void => {},
  onOpenWarningSummary = (): void => {},
  onSaveDraft = async (formValues: WarningEvent): Promise<null> => null,
  onPublish = async (formValues: WarningEvent): Promise<null> => null,
  isSummaryDialogOpen = false,
  selectedEvent,
}) => {
  const { t } = useWarningsTranslation();
  const confirmDialog = useConfirmationDialog();
  const [isPublishing, setIsPublishing] = React.useState(false);
  const [isUnpublishedExpiredWithdraw, setIsUnpublishedExpiredWithdraw] =
    React.useState(false);

  const { toggleIsDraft, DraftFieldHelper } = useDraftFormHelpers();

  const {
    control,
    getValues,
    handleSubmit,
    formState,
    reset,
    resetField,
    setValue,
    trigger,
  } = useFormContext<WarningEvent>();

  const { dirtyFields, errors: fieldErrors } = formState;
  const isDirty = Object.keys(dirtyFields).length > 0;

  const showSummaryError = getCrossFieldError(
    'texts.headlineOriginal',
    fieldErrors,
  );
  const showTranslationError = getCrossFieldError(
    'texts.headlineTranslation',
    fieldErrors,
  );

  usePreventBrowserClose(isDirty);
  const ref = useScrollTopOnError(error);
  const errors = getErrors(error.message);

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'warnings',
  });

  // When switching event, reset the form to the new values
  const eventId = selectedEvent?.eventId;
  React.useEffect(() => {
    // sync redux with react-hook-form
    reset(getFormData(selectedEvent));
    // TODO once we implement areas
    // setOriginalWarningAreas(publicWarningDetails?.areas);
  }, [eventId, reset, selectedEvent]);

  // When the editor gets set back to viewer and the form is dirty, reset any changes made
  React.useEffect(() => {
    if (
      isReadOnly &&
      Object.keys(dirtyFields).length > 0
      // TODO once we implement areas
      //   ||selectedEvent?.areas !== originalWarningAreas)
    ) {
      // // sync redux with react-hook-form
      const oldEvent = {
        ...selectedEvent,
        // areas: originalWarningAreas,
      } as WarningEvent;
      reset(getFormData(oldEvent));
      // TODO once we implement areas
      // if (publicWarningDetails?.areas !== originalWarningAreas) {
      //   // update the store to contain the original area again
      //   onSetViewModeResetArea(originalWarningAreas || []);
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly]);

  const warningEventType = useWatch({ control, name: 'warningEventType' });
  const [selectedDays, setSelectedDays] = React.useState<[number, number] | []>(
    () => getDefaultDayRangeForType(getValues('warningEventType')),
  );

  const [compactIds, setCompactIds] = React.useState<Record<string, boolean>>(
    () =>
      isDefaultCollapsed
        ? Object.fromEntries(fields.map((field) => [field.id, true]))
        : {},
  );

  const handleWarningTypeChange = (nextType: WarningEventType): void => {
    setSelectedDays(getDefaultDayRangeForType(nextType));
  };

  const texts = useWatch({ control, name: 'texts' });

  const updateSummaryTexts = (
    nextMode: WarningEventMode,
    currentTexts: WarningEventTexts,
  ): void => {
    (Object.keys(currentTexts) as (keyof WarningEventTexts)[]).forEach(
      (field) => {
        const isTranslationField = field.includes('Translation');
        const prefix = isTranslationField
          ? TEST_MESSAGE_PREFIX_TRANSLATION
          : TEST_MESSAGE_PREFIX_ORIGINAL;
        const value = currentTexts[field] ?? '';

        if (nextMode === WarningEventMode.TEST) {
          if (!value.startsWith(prefix)) {
            setValue(`texts.${field}`, `${prefix}${value}`, {
              shouldDirty: true,
            });
          }
        } else if (nextMode === WarningEventMode.ACTUAL) {
          if (value.startsWith(prefix)) {
            setValue(`texts.${field}`, value.slice(prefix.length), {
              shouldDirty: true,
            });
          }
        }
      },
    );
  };

  const handleWarningModeChange = (mode: WarningEventMode): void => {
    updateSummaryTexts(mode, texts);
  };

  const allCompact =
    fields.length > 0 && fields.every((field) => compactIds[field.id]);

  const toggleCompact = (id: string): void => {
    setCompactIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleToggleAllCompact = (): void => {
    setCompactIds(
      allCompact
        ? {}
        : Object.fromEntries(fields.map((field) => [field.id, true])),
    );
  };

  const handleToggleDay = (day: number): void => {
    setSelectedDays((prev) => {
      if (prev.length === 0) {
        return [day, day];
      }
      if (prev.length === 2 && prev[0] === day && prev[1] === day) {
        return [];
      }
      return [prev[0], day];
    });
  };

  const timelineRange = React.useMemo(
    () => getTimelineRange(selectedDays),
    [selectedDays],
  );

  const onAddWarning = (): void => {
    append(buildDefaultWarningItem(getValues()));
  };

  const onDeleteWarning = (index: number): void => {
    remove(index);
  };

  useFormDirty({
    isDirty: Object.keys(dirtyFields).length > 0,
    isLoading: isPublishing,
    error: error?.message,
    onFormDirty,
    onSuccess: () => {
      // reset form dirty and update defaultvalues and formvalues after successfull saving
      reset(getValues());
    },
  });

  const handleSaveDraft = (): void => {
    if (!isPublishing) {
      toggleIsDraft(true);
      void handleSubmit(async (data) => {
        setIsPublishing(true);
        const valuesToPost = buildSubmitValues(data);
        await onSaveDraft(valuesToPost).catch(() => {});
        setIsPublishing(false);
      })();
    }
  };

  const handlePublish = handleSubmit((values) => {
    if (!isPublishing) {
      toggleIsDraft(true);
      void handleSubmit(async (data) => {
        setIsPublishing(true);
        const valuesToPost = buildSubmitValues(data);
        await onPublish(valuesToPost).catch(() => {});
        setIsPublishing(false);
      })();
    }
  });

  const onExpireWithdraw = (
    status: PublicWarningStatus,
    index: number,
  ): void => {
    const titleStatus =
      status === PublicWarningStatus.EXPIRED ? 'expire' : 'withdraw';
    void confirmDialog({
      title: t(`warning-event-${titleStatus}-confirm-title`),
      description: t(`warning-event-${titleStatus}-confirm-description`),
      confirmLabel: t(`warning-event-${titleStatus}-confirm-confirm`),
      cancelLabel: t('warning-dialog-cancel'),
    }).then(() => {
      resetField(`warnings.${index}`);
      setValue(`warnings.${index}.status`, status, {
        shouldDirty: true,
      });
      setIsUnpublishedExpiredWithdraw(true);
    });
  };

  const onChangeMode = (): void => {
    onChangeFormMode(!isReadOnly);
  };

  // Until we implement the full validation, set every field to be required when publishing
  const { isRequired } = useDraftFormHelpers();

  const disabledWarningDeleteButton = fields.length <= 1;

  return (
    <Box
      data-testid="warning-event-form-scroll-container"
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'geowebColors.background.surfaceApp',
      }}
    >
      {!!errors && (
        <Box sx={{ marginBottom: 2 }}>
          <AlertBanner
            info={errors.errors as unknown as React.ReactElement}
            severity={error?.severity}
            shouldClose
            title={errors.title}
          />
        </Box>
      )}
      {!shouldHideFormToggleMode ? (
        <Box
          sx={{
            flexShrink: 0,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'flex-end',
            height: '30px',
            paddingRight: 0.5,
          }}
        >
          <Box sx={{ position: 'relative', top: -2, right: '16px' }}>
            <SwitchButton
              activeLabel={t('warning-editor-user-editor')}
              checked={!isReadOnly}
              inActiveLabel={t('warning-editor-user-viewer')}
              onChange={onChangeMode}
            />
          </Box>
          <Box
            sx={{
              position: 'relative',
              top: 8,
              right: '8px',
              width: '16px',
            }}
          >
            {!!currentEventEditor && (
              <WarningAvatar editor={currentEventEditor} />
            )}
          </Box>
        </Box>
      ) : null}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingX: 1,
          paddingY: 0.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'geowebColors.background.surface',
            borderRadius: 1,
            padding: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'top',
                gap: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box sx={{ flex: '0.7 1 50px', position: 'relative' }}>
                <ReactHookFormSelect
                  disabled={isReadOnly}
                  label={t('public-warning-event-warning-mode')}
                  name="mode"
                  onChange={(event) =>
                    handleWarningModeChange(
                      event.target.value as WarningEventMode,
                    )
                  }
                >
                  {warningEventModes.map((mode) => (
                    <MenuItem key={mode.key} value={mode.key}>
                      {t(mode.translationKey)}
                    </MenuItem>
                  ))}
                </ReactHookFormSelect>
              </Box>
              <Box sx={{ flex: '1 1 110px', position: 'relative' }}>
                <ReactHookFormTextField
                  disabled={isReadOnly}
                  label={t('public-warning-event-incident')}
                  name="incident"
                  onChange={async (): Promise<boolean> => trigger('incident')}
                  rules={{
                    validate: {
                      isOverMaxLength: (value): boolean | string =>
                        value.length > INCIDENT_MAX_LENGTH
                          ? t('warning-event-validation-incident-max-length', {
                              MAX_CHARS: INCIDENT_MAX_LENGTH,
                            })
                          : true,
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: '1.3 1 120px',
                  position: 'relative',
                  '& .MuiSelect-select .MuiGrid-container': {
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    flexDirection: 'row-reverse',
                    justifyContent: 'flex-end !important',
                    gap: 1,
                  },
                  '& .MuiSelect-select .MuiGrid-container > .MuiGrid-root:last-child':
                    {
                      minWidth: 'auto',
                    },
                  '& .MuiSelect-select .MuiSvgIcon-root': {
                    fontSize: '1.2rem',
                  },
                }}
              >
                <Phenomenon isDisabled={isReadOnly} isReadOnly={isReadOnly} />
              </Box>
              <Box sx={{ flex: '1.3 1 50' }}>
                <ReactHookFormSelect
                  disabled={isReadOnly}
                  label={t('public-warning-event-warning-type')}
                  name="warningEventType"
                  onChange={(event) =>
                    handleWarningTypeChange(
                      event.target.value as WarningEventType,
                    )
                  }
                >
                  {warningEventTypes.map((type) => (
                    <MenuItem key={type.key} value={type.key}>
                      {t(type.translationKey)}
                    </MenuItem>
                  ))}
                </ReactHookFormSelect>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'top',
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                <DaySelector
                  minSelectableDay={getMinSelectableDayForType(
                    warningEventType,
                  )}
                  onToggleDay={handleToggleDay}
                  selectedDays={selectedDays}
                />
                <Box
                  sx={{
                    display: 'flex',
                    paddingTop: '16px',
                  }}
                >
                  <CustomIconButton disabled={isReadOnly} variant="tool">
                    <AlertBadge />
                  </CustomIconButton>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexShrink: 0,
                height: 56,
              }}
            >
              <CollapseToggleButton
                collapseLabel={t('public-warning-event-collapse-all')}
                expandLabel={t('public-warning-event-expand-all')}
                isCollapsed={allCompact}
                onToggle={handleToggleAllCompact}
              />
            </Box>
          </Box>

          <Grid
            columnSpacing={2}
            container
            rowSpacing={2}
            sx={{ marginTop: 0.5 }}
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ position: 'relative' }}>
                <ReactHookFormTextField
                  disabled={isReadOnly}
                  label={t('public-warning-event-summary')}
                  multiline
                  name="texts.headlineOriginal"
                  rows={3}
                  rules={{
                    maxLength: SUMMARY_HEADLINE_LIMIT,
                    validate: {
                      isRequired,
                    },
                  }}
                />
                {!!showSummaryError && (
                  <FormHelperText error variant="filled">
                    {t(showSummaryError)}
                  </FormHelperText>
                )}
                <CustomIconButton
                  data-testid="warning-event-form-summary-button"
                  isSelected={isSummaryDialogOpen}
                  onClick={onOpenWarningSummary}
                  sx={{ position: 'absolute', right: 4, top: 73, zIndex: 1 }}
                  tooltipTitle={t('warning-event-open-summary-dialog-tooltip')}
                  variant="tool"
                >
                  <Edit />
                </CustomIconButton>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReactHookFormTextField
                disabled={isReadOnly}
                label={t('public-warning-event-translation')}
                multiline
                name="texts.headlineTranslation"
                rows={3}
                rules={{
                  maxLength: SUMMARY_HEADLINE_LIMIT,
                  validate: {
                    isRequired,
                  },
                }}
              />
              {!!showTranslationError && (
                <FormHelperText error variant="filled">
                  {t(showTranslationError)}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        </Box>
        <WarningEventTimelineRangeProvider range={timelineRange}>
          <ReactSortable
            animation={200}
            direction="vertical"
            handle=".handle"
            list={fields as { id: string }[]}
            onUpdate={({ oldIndex, newIndex }) => {
              if (
                oldIndex === undefined ||
                newIndex === undefined ||
                oldIndex === newIndex ||
                newIndex < 0 ||
                newIndex >= fields.length
              ) {
                return;
              }
              move(oldIndex, newIndex);
            }}
            setList={() => {}}
            tag="div"
          >
            {fields.map((field, index) => (
              <div key={field.id}>
                <WarningEventItem
                  defaultMapPreset={defaultMapPreset}
                  index={index}
                  isCompact={!!compactIds[field.id]}
                  isDeleteButtonDisabled={disabledWarningDeleteButton}
                  isReadOnly={isReadOnly}
                  isSnapshot={isSnapshot}
                  onDeleteWarning={onDeleteWarning}
                  onExpireWithdraw={onExpireWithdraw}
                  onToggleCompact={() => toggleCompact(field.id)}
                />
              </div>
            ))}
          </ReactSortable>
        </WarningEventTimelineRangeProvider>

        {!isReadOnly && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={onAddWarning}
              startIcon={<Add />}
              sx={{ textTransform: 'none', fontSize: '12px' }}
              variant="tertiary"
            >
              {t('public-warning-event-add-warning')}
            </Button>
          </Box>
        )}
      </Box>
      <DraftFieldHelper />
      <ReactHookFormHiddenInput
        name="texts.conciseSummaryOriginal"
        rules={{
          maxLength: SUMMARY_CONCISE_SUMMARY_LIMIT,
          validate: {
            isRequired,
          },
        }}
      />
      <ReactHookFormHiddenInput
        name="texts.conciseSummaryTranslation"
        rules={{
          maxLength: SUMMARY_CONCISE_SUMMARY_LIMIT,
          validate: {
            isRequired,
          },
        }}
      />
      <ReactHookFormHiddenInput
        name="texts.descriptionOriginal"
        rules={{
          maxLength: SUMMARY_DESCRIPTION_LIMIT,
          validate: {
            isRequired: (value: string): boolean | string =>
              warningEventType === WarningEventType.EARLY_WARNING
                ? true
                : isRequired(value),
          },
        }}
      />
      <ReactHookFormHiddenInput
        name="texts.descriptionTranslation"
        rules={{
          maxLength: SUMMARY_DESCRIPTION_LIMIT,
          validate: {
            isRequired: (value: string): boolean | string =>
              warningEventType === WarningEventType.EARLY_WARNING
                ? true
                : isRequired(value),
          },
        }}
      />
      {!isReadOnly && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              '& button:not(:last-child)': { mr: 3 },
            }}
          >
            <Button
              disabled={isPublishing}
              onClick={onCancel}
              sx={{ textTransform: 'none', fontSize: '12px' }}
              variant="tertiary"
            >
              {t('public-warning-event-button-cancel')}
            </Button>
            {
              // Only show delete button if event has been stored already and is in DRAFT status
              selectedEvent?.eventId &&
              (selectedEvent?.status === PublicWarningStatus.DRAFT ||
                selectedEvent?.status ===
                  PublicWarningStatus.DRAFT_PUBLISHED) ? (
                <Button
                  color="error"
                  disabled={isPublishing}
                  onClick={onDelete}
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                  variant="tertiary"
                >
                  {t('public-warning-event-button-delete')}
                </Button>
              ) : null
            }
            <Tooltip
              title={
                isUnpublishedExpiredWithdraw
                  ? t('warning-event-save-draft-disabled-withdraw-expire')
                  : ''
              }
            >
              <Box sx={{ marginRight: '24px' }}>
                <Button
                  disabled={isPublishing || isUnpublishedExpiredWithdraw}
                  onClick={handleSaveDraft}
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                  variant="tertiary"
                >
                  {t('public-warning-event-button-save-draft')}
                </Button>
              </Box>
            </Tooltip>
            <Button
              disabled={isPublishing}
              onClick={handlePublish}
              sx={{ textTransform: 'none', fontSize: '12px' }}
              variant="primary"
            >
              {t('public-warning-event-button-publish')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
